import {Badge, Box, Button, Group, Paper, Text, Tooltip} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {trackEvent} from 'analytics/Analytics';
import {MODEL_HUB_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import legacyRunModal from 'ui-library/legacyRunModal';

import BulkPredictionModal from './input-forms/BulkPredictionModal';
import {ScientificModelInputFormModal} from './input-forms/InputForm';
import type {ScientificModelNode} from './ScientificModelMetadata';

const INPUT_TYPE_LABELS: Record<string, string> = {
  amino_acid_sequence: 'AA Sequence',
  molecule: 'Molecule',
};

const CATEGORY_LABELS: Record<string, string> = {
  structure_prediction: 'Structure Prediction',
  generative: 'Generative',
};

export interface ScientificModelCardProps {
  model: ScientificModelNode;
  onNavigateToRuns?: () => void;
}

export interface CustomScientificModelCardProps {
  displayName: string;
  iconPath: string;
  systemName: string;
  description: string;
  paperLink: string;
  inputTypes: Array<string>;
  category: string;
  onButtonClick: () => void;
  buttonLabel?: string;
  model?: never;
}

export function ScientificModelCard(
  props: ScientificModelCardProps | CustomScientificModelCardProps
): JSX.Element {
  const card =
    props.model != null
      ? {
          category: props.model.category,
          description: props.model.description,
          displayName: props.model.displayName,
          iconPath: props.model.iconPath,
          inputTypes: props.model.inputTypes,
          paperLink: props.model.paperLink,
        }
      : props;
  const {category, description, displayName, iconPath, inputTypes, paperLink} = card;
  const buttonLabel = props.model != null ? undefined : props.buttonLabel;

  const handleRunModelClick = async (): Promise<void> => {
    if (props.model == null) {
      props.onButtonClick();
      return;
    }
    const scientificModel = props.model;
    trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.MODEL_HUB_MODEL_CARD_CLICKED, {
      model: scientificModel.externalReference,
      category,
    });
    if (scientificModel.capabilities.supportsBulkPredictions) {
      const result = await legacyRunModal(BulkPredictionModal, {
        model: scientificModel,
      });
      if (result != null) {
        props.onNavigateToRuns?.();
      }
    } else {
      legacyRunModal(ScientificModelInputFormModal, {
        model: scientificModel,
      });
    }
  };

  const handlePaperLinkClick = (e: React.MouseEvent): void => {
    e.stopPropagation();
    window.open(paperLink, '_blank', 'noopener,noreferrer');
  };

  return (
    <Paper component="div" className={css(styles.card)} withBorder={true} radius="md" p="md">
      <div className={css(styles.cardContent)}>
        <div>
          <Group spacing="sm" noWrap={true} align="center" mb="xs">
            <Box className={css(styles.iconContainer)}>
              <img src={iconPath} alt={displayName} className={css(styles.icon)} />
            </Box>
            <Text weight={600} className={css(styles.modelName)}>
              {displayName}
            </Text>
          </Group>

          <Group spacing={6} mb="xs">
            <Badge
              size="sm"
              variant="light"
              radius="md"
              color="indigo"
              styles={{root: {fontSize: 12, fontWeight: 600}}}
            >
              {CATEGORY_LABELS[category] ?? category}
            </Badge>
            {inputTypes.map((inputType) => (
              <Badge
                key={inputType}
                size="sm"
                variant="light"
                radius="md"
                color="indigo"
                styles={{root: {fontSize: 12, fontWeight: 600}}}
              >
                {INPUT_TYPE_LABELS[inputType] ?? inputType}
              </Badge>
            ))}
          </Group>

          <Tooltip
            label={description}
            multiline={true}
            width={300}
            withArrow={true}
            position="bottom"
            openDelay={300}
          >
            <Text size="sm" color="dimmed" className={css(styles.description)}>
              {description}
            </Text>
          </Tooltip>
        </div>

        <Group position="apart" align="center" mt="sm">
          {paperLink ? (
            <Group spacing={4}>
              <Text
                component="a"
                href={paperLink}
                target="_blank"
                rel="noopener noreferrer"
                size="xs"
                color="blue"
                className={css(styles.paperLink)}
                onClick={handlePaperLinkClick}
              >
                Reference
              </Text>
            </Group>
          ) : (
            <div />
          )}
          <Button size="xs" radius="sm" onClick={handleRunModelClick}>
            {buttonLabel ?? 'Run Batch'}
          </Button>
        </Group>
      </div>
    </Paper>
  );
}

const styles = StyleSheet.create({
  card: {
    userSelect: 'none',
    width: 380,
    minWidth: 380,
    flex: '0 0 380px',
    maxWidth: 380,
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    height: '100%',
    minHeight: 200,
  },
  iconContainer: {
    width: 48,
    height: 48,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    backgroundColor: '#f5f5f5',
    borderRadius: 6,
  },
  icon: {
    maxWidth: '80%',
    maxHeight: '80%',
    objectFit: 'contain',
  },
  modelName: {
    fontSize: 18,
    lineHeight: 1.2,
  },
  description: {
    lineHeight: 1.4,
    fontSize: 13,
    display: '-webkit-box' as 'block',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  paperLink: {
    textDecoration: 'none',
    ':hover': {
      textDecoration: 'underline',
    },
  },
});
